import type {
  ActivityEventType,
  ActivityFeedDto,
  AddBookingNoteDto,
  AttachSiteImageDto,
  AvailableActivityMonthDto,
  AvailableReportMonthDto,
  BookingDeletionPreviewDto,
  BookingEditLogEntryDto,
  CancelBookingDto,
  CancelBookingResultDto,
  ChangeBookingCustomerDto,
  CreatePlaceDto,
  DeletedBookingArchiveDto,
  DeleteBookingDto,
  DeleteBookingResultDto,
  CreateUploadUrlDto,
  PlaceDto,
  ReorderPlacesDto,
  ReorderSiteImagesDto,
  SetTourPlacesDto,
  SiteImageDto,
  SiteImageSlot,
  UpdatePlaceDto,
  UpdateSiteImageDto,
  UpdateWebsiteSettingsDto,
  UploadUrlDto,
  WebsiteSettingsDto,
  AddLeadActivityDto,
  BookingDto,
  ConvertLeadResultDto,
  CreateBookingDto,
  CreateCreditNoteDto,
  CreateUserDto,
  UpdateUserDto,
  AttachTourImageDto,
  AttachTourMediaDto,
  CreateCustomerDto,
  CreateLeadDto,
  CreateDepartureDto,
  CreateTourDto,
  CreditNoteDto,
  ItineraryDayInputDto,
  LinkVendorDto,
  PricingTierInputDto,
  PublishChecklistDto,
  ReorderTourImagesDto,
  SetLiveOnWebsiteDto,
  UpdateDepartureDto,
  UpdateTourImageDto,
  UpdateTourSlugDto,
  UpdateTourWebsiteDto,
  AddExpensePaymentDto,
  BookingProfitDto,
  CreateExpenseDto,
  CustomerDto,
  DeletedTourDto,
  DepartureProfitDto,
  DocumentEditLogEntryDto,
  EditBookingDto,
  EditBookingPreviewDto,
  EditBookingResultDto,
  ExpenseDto,
  ExpenseListDto,
  ExpenseListFiltersDto,
  ExpenseSummaryDto,
  LeadActivityDto,
  PayablesAgingDto,
  ProfitBasis,
  UpdateExpenseDto,
  VoidExpenseDto,
  PaymentLedgerDto,
  ReceiptDto,
  RecordPaymentDto,
  ReviseBookingValueDto,
  UpdateInvoiceDto,
  UpdateInvoiceSettingsDto,
  UpdateVoucherDto,
  VoucherDto,
  UpdateBookingStatusDto,
  UpdateCustomerDto,
  UpdateLeadDto,
  UpdateTourDto,
  DashboardMonthlyOverviewDto,
  DashboardSummaryDto,
  GlobalSearchResultsDto,
  InvoiceDto,
  InvoiceSettingsDto,
  InvoiceSummaryDto,
  LeadDto,
  LoginRequestDto,
  LoginResponseDto,
  MarkAllReadResultDto,
  MarkEventReadResultDto,
  MeResponseDto,
  NotificationListDto,
  PaymentDto,
  PaymentSummaryDto,
  PermissionMap,
  PermissionMatrixDto,
  PermissionMatrixUpdateDto,
  ReportsSummaryDto,
  TeamMemberDto,
  TourDto,
  VoucherWithInvoiceDto,
} from '@gty/common';

import type {
  ShareDocumentKind,
  ShareLinkDto,
  ShareLinkSummaryDto,
} from '../documents/share-link';
import type { ChequeActionRequestDto } from '../payments/cheque-actions';
import { apiFetch } from './client';

/**
 * Every endpoint, in one place.
 *
 * Paths are written WITHOUT the `/api` prefix — `apiUrl()` owns the base path,
 * so the prefix exists in exactly one file and cannot drift.
 */

export function login(payload: LoginRequestDto): Promise<LoginResponseDto> {
  return apiFetch<LoginResponseDto>('/auth/login', {
    method: 'POST',
    body: payload,
    // A 401 here means "wrong password", not "token expired". Refreshing would
    // be nonsense and would hide the real error from the login form.
    skipAuthRefresh: true,
  });
}

export function logout(): Promise<void> {
  return apiFetch<void>('/auth/logout', { method: 'POST', skipAuthRefresh: true });
}

export function fetchMe(): Promise<MeResponseDto> {
  return apiFetch<MeResponseDto>('/auth/me');
}

export function fetchMyPermissions(): Promise<PermissionMap> {
  return apiFetch<PermissionMap>('/permissions/me');
}

export function fetchDashboardSummary(signal?: AbortSignal): Promise<DashboardSummaryDto> {
  return apiFetch<DashboardSummaryDto>('/dashboard/summary', signal ? { signal } : {});
}

/** The month carousel (Part B) — one call, every month with data. */
export function fetchDashboardMonths(signal?: AbortSignal): Promise<DashboardMonthlyOverviewDto> {
  return apiFetch<DashboardMonthlyOverviewDto>('/dashboard/months', signal ? { signal } : {});
}

/**
 * GLOBAL SEARCH — the one endpoint that spans four modules.
 *
 * `q` is the raw term; the server trims it and echoes back what it actually
 * searched for as `results.query`. The `signal` is NOT optional in practice:
 * this fires on a typeahead, so every superseded keystroke's request must be
 * abortable or the browser's six-connection limit becomes the bottleneck.
 *
 * There is no client-side length guard here on purpose. Below
 * `SEARCH_MIN_QUERY_LENGTH` the server answers `tooShort: true` with empty
 * groups and never touches the database, so "should this call happen at all" is
 * a UI decision (`useGlobalSearch` gates it) and not a rule duplicated in two
 * layers that can drift.
 */
export function searchGlobal(q: string, signal?: AbortSignal): Promise<GlobalSearchResultsDto> {
  return apiFetch<GlobalSearchResultsDto>(
    `/search?q=${encodeURIComponent(q)}`,
    signal ? { signal } : {},
  );
}

/* ── business modules ─────────────────────────────────────────────────────── */

/**
 * Detail routes take either the uuid or the human ref ("BK-2607-0088"), because
 * the design links to records by the reference it prints. `encodeURIComponent`
 * is not optional here: refs are user-facing strings, and one containing a
 * slash would otherwise silently address a different route.
 */
function byId<T>(base: string, id: string, signal?: AbortSignal): Promise<T> {
  return apiFetch<T>(`${base}/${encodeURIComponent(id)}`, signal ? { signal } : {});
}

export function fetchLeads(signal?: AbortSignal): Promise<LeadDto[]> {
  return apiFetch<LeadDto[]>('/leads', signal ? { signal } : {});
}

export function fetchLead(id: string, signal?: AbortSignal): Promise<LeadDto> {
  return byId('/leads', id, signal);
}

export function fetchCustomers(signal?: AbortSignal): Promise<CustomerDto[]> {
  return apiFetch<CustomerDto[]>('/customers', signal ? { signal } : {});
}

export function fetchCustomer(id: string, signal?: AbortSignal): Promise<CustomerDto> {
  return byId('/customers', id, signal);
}

export function fetchTours(signal?: AbortSignal): Promise<TourDto[]> {
  return apiFetch<TourDto[]>('/tours', signal ? { signal } : {});
}

export function fetchTour(id: string, signal?: AbortSignal): Promise<TourDto> {
  return byId('/tours', id, signal);
}

export function fetchBookings(signal?: AbortSignal): Promise<BookingDto[]> {
  return apiFetch<BookingDto[]>('/bookings', signal ? { signal } : {});
}

export function fetchBooking(id: string, signal?: AbortSignal): Promise<BookingDto> {
  return byId('/bookings', id, signal);
}

export function fetchInvoices(signal?: AbortSignal): Promise<InvoiceDto[]> {
  return apiFetch<InvoiceDto[]>('/invoices', signal ? { signal } : {});
}

export function fetchInvoice(id: string, signal?: AbortSignal): Promise<InvoiceDto> {
  return byId('/invoices', id, signal);
}

export function fetchInvoiceSummary(signal?: AbortSignal): Promise<InvoiceSummaryDto> {
  return apiFetch<InvoiceSummaryDto>('/invoices/summary', signal ? { signal } : {});
}

export function fetchVouchers(signal?: AbortSignal): Promise<VoucherWithInvoiceDto[]> {
  return apiFetch<VoucherWithInvoiceDto[]>('/vouchers', signal ? { signal } : {});
}

export function fetchVoucher(id: string, signal?: AbortSignal): Promise<VoucherWithInvoiceDto> {
  return byId('/vouchers', id, signal);
}

export function fetchCreditNotes(signal?: AbortSignal): Promise<CreditNoteDto[]> {
  return apiFetch<CreditNoteDto[]>('/credit-notes', signal ? { signal } : {});
}

export function fetchCreditNote(id: string, signal?: AbortSignal): Promise<CreditNoteDto> {
  return byId('/credit-notes', id, signal);
}

export function fetchPayments(signal?: AbortSignal): Promise<PaymentDto[]> {
  return apiFetch<PaymentDto[]>('/payments', signal ? { signal } : {});
}

export function fetchPaymentSummary(signal?: AbortSignal): Promise<PaymentSummaryDto> {
  return apiFetch<PaymentSummaryDto>('/payments/summary', signal ? { signal } : {});
}

export function fetchPaymentsForCustomer(
  customerId: string,
  signal?: AbortSignal,
): Promise<PaymentDto[]> {
  return apiFetch<PaymentDto[]>(
    `/payments/for-customer/${encodeURIComponent(customerId)}`,
    signal ? { signal } : {},
  );
}

export function fetchReportsSummary(signal?: AbortSignal): Promise<ReportsSummaryDto> {
  return apiFetch<ReportsSummaryDto>('/reports/summary', signal ? { signal } : {});
}

export function fetchReportMonths(signal?: AbortSignal): Promise<AvailableReportMonthDto[]> {
  return apiFetch<AvailableReportMonthDto[]>('/reports/months', signal ? { signal } : {});
}

/** The Reports page's monthly overview table (Part C) — same shared data the Dashboard carousel uses. */
export function fetchMonthlyOverview(signal?: AbortSignal): Promise<DashboardMonthlyOverviewDto> {
  return apiFetch<DashboardMonthlyOverviewDto>('/reports/monthly-overview', signal ? { signal } : {});
}

/* ── write paths ──────────────────────────────────────────────────────────── */

export function createLead(payload: CreateLeadDto): Promise<LeadDto> {
  return apiFetch<LeadDto>('/leads', { method: 'POST', body: payload });
}

export function updateLead(id: string, payload: UpdateLeadDto): Promise<LeadDto> {
  return apiFetch<LeadDto>(`/leads/${encodeURIComponent(id)}`, { method: 'PATCH', body: payload });
}

export function fetchLeadActivities(id: string, signal?: AbortSignal): Promise<LeadActivityDto[]> {
  return apiFetch<LeadActivityDto[]>(
    `/leads/${encodeURIComponent(id)}/activities`,
    signal ? { signal } : {},
  );
}

export function addLeadActivity(id: string, payload: AddLeadActivityDto): Promise<LeadActivityDto> {
  return apiFetch<LeadActivityDto>(`/leads/${encodeURIComponent(id)}/activities`, {
    method: 'POST',
    body: payload,
  });
}

export function convertLead(id: string): Promise<ConvertLeadResultDto> {
  return apiFetch<ConvertLeadResultDto>(`/leads/${encodeURIComponent(id)}/convert`, {
    method: 'POST',
  });
}

export function createCustomer(payload: CreateCustomerDto): Promise<CustomerDto> {
  return apiFetch<CustomerDto>('/customers', { method: 'POST', body: payload });
}

export function updateCustomer(id: string, payload: UpdateCustomerDto): Promise<CustomerDto> {
  return apiFetch<CustomerDto>(`/customers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function createTour(payload: CreateTourDto): Promise<TourDto> {
  return apiFetch<TourDto>('/tours', { method: 'POST', body: payload });
}

export function updateTour(id: string, payload: UpdateTourDto): Promise<TourDto> {
  return apiFetch<TourDto>(`/tours/${encodeURIComponent(id)}`, { method: 'PATCH', body: payload });
}

/**
 * Retire a tour. Soft delete on the server; the response is deliberately not a
 * `TourDto` because the row no longer satisfies the `deletedAt: null` filter
 * every tour read uses.
 */
export function deleteTour(id: string): Promise<DeletedTourDto> {
  return apiFetch<DeletedTourDto>(`/tours/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Tour child collections. Itinerary and tiers are replaced whole — see the service. */
export function replaceItinerary(id: string, days: ItineraryDayInputDto[]): Promise<TourDto> {
  return apiFetch<TourDto>(`/tours/${encodeURIComponent(id)}/itinerary`, {
    method: 'PUT',
    body: { days },
  });
}

export function replaceTiers(id: string, tiers: PricingTierInputDto[]): Promise<TourDto> {
  return apiFetch<TourDto>(`/tours/${encodeURIComponent(id)}/tiers`, {
    method: 'PUT',
    body: { tiers },
  });
}

export function addDeparture(id: string, payload: CreateDepartureDto): Promise<TourDto> {
  return apiFetch<TourDto>(`/tours/${encodeURIComponent(id)}/departures`, {
    method: 'POST',
    body: payload,
  });
}

export function updateDeparture(
  id: string,
  departureId: string,
  payload: UpdateDepartureDto,
): Promise<TourDto> {
  return apiFetch<TourDto>(
    `/tours/${encodeURIComponent(id)}/departures/${encodeURIComponent(departureId)}`,
    { method: 'PATCH', body: payload },
  );
}

export function removeDeparture(id: string, departureId: string): Promise<TourDto> {
  return apiFetch<TourDto>(
    `/tours/${encodeURIComponent(id)}/departures/${encodeURIComponent(departureId)}`,
    { method: 'DELETE' },
  );
}

export function linkVendor(id: string, payload: LinkVendorDto): Promise<TourDto> {
  return apiFetch<TourDto>(`/tours/${encodeURIComponent(id)}/vendors`, {
    method: 'POST',
    body: payload,
  });
}

export function unlinkVendor(id: string, vendorId: string): Promise<TourDto> {
  return apiFetch<TourDto>(
    `/tours/${encodeURIComponent(id)}/vendors/${encodeURIComponent(vendorId)}`,
    { method: 'DELETE' },
  );
}

/**
 * The multipart part name the brochure upload route reads the file from.
 *
 * Same value and same reasoning as `QR_IMAGE_FORM_FIELD` below, and deliberately
 * NOT a shared constant between the two: they are two independent contracts with
 * two different `FileInterceptor('file', ...)` calls on the server, and one
 * route renaming its part must not appear to rename the other's. Named at all,
 * rather than inlined, because a mismatch fails as "no file was uploaded" rather
 * than as a type error.
 */
export const TOUR_BROCHURE_FORM_FIELD = 'file';

/**
 * Attach or replace the tour's customer-facing brochure PDF.
 *
 * `multipart/form-data`, not base64-in-JSON, for the same reason as the QR
 * upload and rather more so: a brochure may be up to 8 MB, which base64 inflates
 * by a third, and the API's JSON body limit is far below either figure — the
 * body parser would reject it before any of our own validation ran.
 *
 * Responds with the committed `TourDto`, so the caller renders the brochure the
 * server actually stored (its sanitised filename, its upload timestamp) rather
 * than a local echo of the file it sent. That matters here: the server rewrites
 * the filename when the original is blank or unsafe, so the name on screen after
 * an upload is frequently NOT the name that was picked.
 *
 * There is no separate "replace" route. Uploading again overwrites — the row is
 * keyed on the tour, one brochure per tour.
 */
export function uploadTourBrochure(tourId: string, file: File): Promise<TourDto> {
  const form = new FormData();
  form.append(TOUR_BROCHURE_FORM_FIELD, file, file.name);
  return apiFetch<TourDto>(`/tours/${encodeURIComponent(tourId)}/brochure`, {
    method: 'POST',
    body: form,
  });
}

/**
 * Remove the tour's brochure.
 *
 * Typed `Promise<void>` and READ AS NOTHING by every caller, DELIBERATELY
 * NOT because the server sends an empty body — it does not: `ToursController.
 * removeBrochure` answers 200 with the full, freshly updated `TourDto`, the
 * same as the upload route (PRE-MERGE ADVERSARY REVIEW, item 2 of the
 * WhatsApp-share wave: an earlier version of this comment claimed "the route
 * may answer 204 with no body at all", which was never true of the actual
 * controller and should not have been written as if it might be). `void` is
 * kept anyway, on purpose: every caller invalidates the tour detail query
 * rather than consuming this response, because that query is keyed on the
 * URL's id-or-ref, which may not be the string this mutation was called
 * with — writing the response straight into the cache would risk seeding a
 * key nothing reads. Typing this `void` makes that the only correct way to
 * use it, rather than an unenforced convention a future caller could miss.
 *
 * A remove path exists at all — unlike the QR image, which is replace-only —
 * because a brochure is customer-facing marketing material: a wrong one being
 * merely un-replaceable, and still servable to anyone holding a link, is a
 * different risk from a wrong internal QR.
 */
export function removeTourBrochure(tourId: string): Promise<void> {
  return apiFetch<void>(`/tours/${encodeURIComponent(tourId)}/brochure`, { method: 'DELETE' });
}

/* ── tour website tab (WEBSITE permission) ───────────────────────────────── */

function websiteBase(tourId: string): string {
  return `/tours/${encodeURIComponent(tourId)}/website`;
}

export function updateTourWebsite(tourId: string, payload: UpdateTourWebsiteDto): Promise<TourDto> {
  return apiFetch<TourDto>(websiteBase(tourId), { method: 'PATCH', body: payload });
}

export function isTourSlugAvailable(tourId: string, slug: string): Promise<{ available: boolean }> {
  const params = new URLSearchParams({ slug });
  return apiFetch<{ available: boolean }>(`${websiteBase(tourId)}/slug-available?${params.toString()}`, {});
}

export function updateTourSlug(tourId: string, payload: UpdateTourSlugDto): Promise<TourDto> {
  return apiFetch<TourDto>(`${websiteBase(tourId)}/slug`, { method: 'PATCH', body: payload });
}

export function setTourCoverImage(tourId: string, payload: AttachTourMediaDto): Promise<TourDto> {
  return apiFetch<TourDto>(`${websiteBase(tourId)}/cover-image`, { method: 'POST', body: payload });
}

export function removeTourCoverImage(tourId: string): Promise<TourDto> {
  return apiFetch<TourDto>(`${websiteBase(tourId)}/cover-image`, { method: 'DELETE' });
}

export function addTourGalleryImage(tourId: string, payload: AttachTourImageDto): Promise<TourDto> {
  return apiFetch<TourDto>(`${websiteBase(tourId)}/images`, { method: 'POST', body: payload });
}

export function removeTourGalleryImage(tourId: string, imageId: string): Promise<TourDto> {
  return apiFetch<TourDto>(`${websiteBase(tourId)}/images/${encodeURIComponent(imageId)}`, {
    method: 'DELETE',
  });
}

export function reorderTourGalleryImages(
  tourId: string,
  payload: ReorderTourImagesDto,
): Promise<TourDto> {
  return apiFetch<TourDto>(`${websiteBase(tourId)}/images/reorder`, { method: 'PATCH', body: payload });
}

export function updateTourGalleryImage(
  tourId: string,
  imageId: string,
  payload: UpdateTourImageDto,
): Promise<TourDto> {
  return apiFetch<TourDto>(`${websiteBase(tourId)}/images/${encodeURIComponent(imageId)}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function setTourWebsiteBrochure(tourId: string, payload: AttachTourMediaDto): Promise<TourDto> {
  return apiFetch<TourDto>(`${websiteBase(tourId)}/brochure`, { method: 'POST', body: payload });
}

export function removeTourWebsiteBrochure(tourId: string): Promise<TourDto> {
  return apiFetch<TourDto>(`${websiteBase(tourId)}/brochure`, { method: 'DELETE' });
}

export function fetchTourPublishChecklist(tourId: string): Promise<PublishChecklistDto> {
  return apiFetch<PublishChecklistDto>(`${websiteBase(tourId)}/publish-checklist`, {});
}

export function setTourLiveOnWebsite(tourId: string, payload: SetLiveOnWebsiteDto): Promise<TourDto> {
  return apiFetch<TourDto>(`${websiteBase(tourId)}/live`, { method: 'PATCH', body: payload });
}

export function setTourPlaces(tourId: string, payload: SetTourPlacesDto): Promise<TourDto> {
  return apiFetch<TourDto>(`${websiteBase(tourId)}/places`, { method: 'PATCH', body: payload });
}

/* ── places ───────────────────────────────────────────────────────────────── */

export function fetchPlaces(signal?: AbortSignal): Promise<PlaceDto[]> {
  return apiFetch<PlaceDto[]>('/places', signal ? { signal } : {});
}

export function fetchPlace(id: string, signal?: AbortSignal): Promise<PlaceDto> {
  return apiFetch<PlaceDto>(`/places/${encodeURIComponent(id)}`, signal ? { signal } : {});
}

export function createPlace(payload: CreatePlaceDto): Promise<PlaceDto> {
  return apiFetch<PlaceDto>('/places', { method: 'POST', body: payload });
}

export function updatePlace(id: string, payload: UpdatePlaceDto): Promise<PlaceDto> {
  return apiFetch<PlaceDto>(`/places/${encodeURIComponent(id)}`, { method: 'PATCH', body: payload });
}

export function reorderPlaces(payload: ReorderPlacesDto): Promise<PlaceDto[]> {
  return apiFetch<PlaceDto[]>('/places/reorder', { method: 'PATCH', body: payload });
}

export function setPlaceImage(id: string, payload: AttachTourMediaDto): Promise<PlaceDto> {
  return apiFetch<PlaceDto>(`/places/${encodeURIComponent(id)}/image`, { method: 'POST', body: payload });
}

export function removePlaceImage(id: string): Promise<PlaceDto> {
  return apiFetch<PlaceDto>(`/places/${encodeURIComponent(id)}/image`, { method: 'DELETE' });
}

export function deletePlace(id: string): Promise<{ deleted: true }> {
  return apiFetch<{ deleted: true }>(`/places/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/* ── site content: site images, site settings ───────────────────────────── */

export function fetchSiteImages(slot?: SiteImageSlot, signal?: AbortSignal): Promise<SiteImageDto[]> {
  const qs = slot === undefined ? '' : `?slot=${encodeURIComponent(slot)}`;
  return apiFetch<SiteImageDto[]>(`/site-content/images${qs}`, signal ? { signal } : {});
}

export function attachSiteImage(payload: AttachSiteImageDto): Promise<SiteImageDto> {
  return apiFetch<SiteImageDto>('/site-content/images', { method: 'POST', body: payload });
}

export function updateSiteImage(id: string, payload: UpdateSiteImageDto): Promise<SiteImageDto> {
  return apiFetch<SiteImageDto>(`/site-content/images/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function removeSiteImage(id: string): Promise<{ deleted: true }> {
  return apiFetch<{ deleted: true }>(`/site-content/images/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function reorderSiteImages(payload: ReorderSiteImagesDto): Promise<SiteImageDto[]> {
  return apiFetch<SiteImageDto[]>('/site-content/images/reorder', { method: 'PATCH', body: payload });
}

export function fetchWebsiteSettings(signal?: AbortSignal): Promise<WebsiteSettingsDto> {
  return apiFetch<WebsiteSettingsDto>('/site-content/settings', signal ? { signal } : {});
}

export function updateWebsiteSettings(payload: UpdateWebsiteSettingsDto): Promise<WebsiteSettingsDto> {
  return apiFetch<WebsiteSettingsDto>('/site-content/settings', { method: 'PATCH', body: payload });
}

/**
 * The bell tray. Derived server-side from live rows — there are no notification
 * records, so there is nothing here that can go stale.
 */
export function fetchNotifications(signal?: AbortSignal): Promise<NotificationListDto> {
  return apiFetch<NotificationListDto>('/notifications', signal ? { signal } : {});
}

/**
 * Mark all of the CURRENT USER's notifications read.
 *
 * No id and no user parameter, deliberately: the server acts on the token
 * holder and nobody else, so there is no argument a caller could get wrong.
 */
export function markAllNotificationsRead(): Promise<MarkAllReadResultDto> {
  return apiFetch<MarkAllReadResultDto>('/notifications/read-all', { method: 'POST' });
}

export interface ActivityFeedQuery {
  from?: string;
  to?: string;
  type?: ActivityEventType;
  actor?: string;
  page?: number;
}

/** The stored activity history — "what happened", not "what needs doing". */
export function fetchActivityFeed(
  query: ActivityFeedQuery = {},
  signal?: AbortSignal,
): Promise<ActivityFeedDto> {
  const params = new URLSearchParams();
  if (query.from !== undefined) params.set('from', query.from);
  if (query.to !== undefined) params.set('to', query.to);
  if (query.type !== undefined) params.set('type', query.type);
  if (query.actor !== undefined) params.set('actor', query.actor);
  if (query.page !== undefined) params.set('page', String(query.page));
  const qs = params.toString();
  return apiFetch<ActivityFeedDto>(`/notifications/feed${qs === '' ? '' : `?${qs}`}`, signal ? { signal } : {});
}

/** Months with at least one stored event — the feed page's month selector. */
export function fetchActivityMonths(signal?: AbortSignal): Promise<AvailableActivityMonthDto[]> {
  return apiFetch<AvailableActivityMonthDto[]>('/notifications/months', signal ? { signal } : {});
}

export function markActivityEventRead(id: string): Promise<MarkEventReadResultDto> {
  return apiFetch<MarkEventReadResultDto>(`/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' });
}

export function createCreditNote(payload: CreateCreditNoteDto): Promise<CreditNoteDto> {
  return apiFetch<CreditNoteDto>('/credit-notes', { method: 'POST', body: payload });
}

/* ── expenses ──────────────────────────────────────────────────────────── */

export function fetchExpenses(filters: ExpenseListFiltersDto, signal?: AbortSignal): Promise<ExpenseListDto> {
  const params = new URLSearchParams();
  if (filters.from !== undefined) params.set('from', filters.from);
  if (filters.to !== undefined) params.set('to', filters.to);
  if (filters.scope !== undefined) params.set('scope', filters.scope);
  if (filters.category !== undefined) params.set('category', filters.category);
  if (filters.bookingRef !== undefined) params.set('bookingRef', filters.bookingRef);
  if (filters.tourId !== undefined) params.set('tourId', filters.tourId);
  if (filters.departureId !== undefined) params.set('departureId', filters.departureId);
  if (filters.vendorId !== undefined) params.set('vendorId', filters.vendorId);
  if (filters.paymentState !== undefined) params.set('paymentState', filters.paymentState);
  if (filters.includeVoided !== undefined) params.set('includeVoided', String(filters.includeVoided));
  if (filters.search !== undefined) params.set('search', filters.search);
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.pageSize !== undefined) params.set('pageSize', String(filters.pageSize));
  const qs = params.toString();
  return apiFetch<ExpenseListDto>(`/expenses${qs === '' ? '' : `?${qs}`}`, signal ? { signal } : {});
}

export function fetchExpense(id: string, signal?: AbortSignal): Promise<ExpenseDto> {
  return apiFetch<ExpenseDto>(`/expenses/${encodeURIComponent(id)}`, signal ? { signal } : {});
}

export function fetchExpenseSummary(
  from?: string,
  to?: string,
  basis?: ProfitBasis,
  signal?: AbortSignal,
): Promise<ExpenseSummaryDto> {
  const params = new URLSearchParams();
  if (from !== undefined) params.set('from', from);
  if (to !== undefined) params.set('to', to);
  if (basis !== undefined) params.set('basis', basis);
  const qs = params.toString();
  return apiFetch<ExpenseSummaryDto>(`/expenses/summary${qs === '' ? '' : `?${qs}`}`, signal ? { signal } : {});
}

export function fetchPayablesAging(signal?: AbortSignal): Promise<PayablesAgingDto> {
  return apiFetch<PayablesAgingDto>('/expenses/payables-aging', signal ? { signal } : {});
}

export function createExpense(payload: CreateExpenseDto): Promise<ExpenseDto> {
  return apiFetch<ExpenseDto>('/expenses', { method: 'POST', body: payload });
}

export function updateExpense(id: string, payload: UpdateExpenseDto): Promise<ExpenseDto> {
  return apiFetch<ExpenseDto>(`/expenses/${encodeURIComponent(id)}/update`, { method: 'POST', body: payload });
}

export function voidExpense(id: string, payload: VoidExpenseDto): Promise<ExpenseDto> {
  return apiFetch<ExpenseDto>(`/expenses/${encodeURIComponent(id)}/void`, { method: 'POST', body: payload });
}

export function addExpensePayment(id: string, payload: AddExpensePaymentDto): Promise<ExpenseDto> {
  return apiFetch<ExpenseDto>(`/expenses/${encodeURIComponent(id)}/payments`, { method: 'POST', body: payload });
}

export function deleteExpensePayment(id: string, paymentId: string): Promise<ExpenseDto> {
  return apiFetch<ExpenseDto>(`/expenses/${encodeURIComponent(id)}/payments/${encodeURIComponent(paymentId)}`, {
    method: 'DELETE',
  });
}

export function fetchBookingProfit(bookingId: string, signal?: AbortSignal): Promise<BookingProfitDto> {
  return apiFetch<BookingProfitDto>(`/bookings/${encodeURIComponent(bookingId)}/profit`, signal ? { signal } : {});
}

/** Departures live under their tour — `/tours/:tourId/departures/:departureId/profit`, not a standalone resource. */
export function fetchDepartureProfit(
  tourId: string,
  departureId: string,
  signal?: AbortSignal,
): Promise<DepartureProfitDto> {
  return apiFetch<DepartureProfitDto>(
    `/tours/${encodeURIComponent(tourId)}/departures/${encodeURIComponent(departureId)}/profit`,
    signal ? { signal } : {},
  );
}

export function createBooking(payload: CreateBookingDto): Promise<BookingDto> {
  return apiFetch<BookingDto>('/bookings', { method: 'POST', body: payload });
}

/**
 * "Mark trip" — append an internal staff note.
 *
 * Returns the WHOLE booking, not the note: the note list lives on `BookingDto`,
 * so handing back the refreshed booking lets the caller replace its cache entry
 * outright instead of splicing an array and hoping the two stay in step.
 */
export function addBookingNote(id: string, payload: AddBookingNoteDto): Promise<BookingDto> {
  return apiFetch<BookingDto>(`/bookings/${encodeURIComponent(id)}/notes`, {
    method: 'POST',
    body: payload,
  });
}

export function updateBookingStatus(
  id: string,
  payload: UpdateBookingStatusDto,
): Promise<BookingDto> {
  return apiFetch<BookingDto>(`/bookings/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: payload,
  });
}

/**
 * Revise the booking's agreed value — the ONLY route that moves its total.
 *
 * POST, not PATCH: a named business action with a mandatory reason, not a
 * partial edit. Guarded by INVOICE_GENERATOR:FULL on the server (Owner, Admin,
 * Accounts), not by BOOKING_MANAGEMENT.
 */
export function reviseBookingValue(
  id: string,
  payload: ReviseBookingValueDto,
): Promise<BookingDto> {
  return apiFetch<BookingDto>(`/bookings/${encodeURIComponent(id)}/revise-value`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Cancel a booking, and settle the money in the same request.
 *
 * NOT `updateBookingStatus({ status: 'CANCELLED' })` — that now 409s. Cancelling
 * releases seats AND decides what happens to money already taken, and the status
 * route has nowhere to put that decision. Guarded by PAYMENTS_RECEIPTS:FULL on
 * the server (Owner, Admin, Accounts), not by BOOKING_MANAGEMENT.
 *
 * Resolves to the refreshed booking PLUS the refs of whatever document the
 * chosen path produced, so the caller can link straight at it.
 */
export function cancelBooking(
  id: string,
  payload: CancelBookingDto,
): Promise<CancelBookingResultDto> {
  return apiFetch<CancelBookingResultDto>(`/bookings/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Live preview for the Edit booking screen — computed from exactly the
 * inputs the save would apply, with no write. Called on every change to the
 * form, so the preview panel never promises something the save then does
 * differently.
 */
export function previewEditBooking(
  id: string,
  payload: Pick<EditBookingDto, 'passengers' | 'total' | 'extraCharges' | 'discount' | 'tourDateId'>,
): Promise<EditBookingPreviewDto> {
  return apiFetch<EditBookingPreviewDto>(`/bookings/${encodeURIComponent(id)}/edit-preview`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * THE FULL "EDIT BOOKING" SAVE — passengers, pricing, departure, all in one
 * request. See `EditBookingDto`'s own note for why this is one action.
 */
export function editBooking(id: string, payload: EditBookingDto): Promise<EditBookingResultDto> {
  return apiFetch<EditBookingResultDto>(`/bookings/${encodeURIComponent(id)}/edit`, {
    method: 'POST',
    body: payload,
  });
}

/** Read-only "Edit history" list on the booking detail screen. */
export function getBookingEditHistory(id: string): Promise<BookingEditLogEntryDto[]> {
  return apiFetch<BookingEditLogEntryDto[]>(`/bookings/${encodeURIComponent(id)}/edit-history`);
}

/** Change the customer a booking belongs to — Owner only on the server. */
export function changeBookingCustomer(id: string, payload: ChangeBookingCustomerDto): Promise<void> {
  return apiFetch<{ ok: true }>(`/bookings/${encodeURIComponent(id)}/change-customer`, {
    method: 'POST',
    body: payload,
  }).then(() => undefined);
}

/**
 * Exactly what "Delete booking" is about to erase — read before the Owner
 * can type the booking number in the confirmation dialog. Owner-only on the
 * server regardless of who can reach the route.
 */
export function getBookingDeletionPreview(id: string): Promise<BookingDeletionPreviewDto> {
  return apiFetch<BookingDeletionPreviewDto>(`/bookings/${encodeURIComponent(id)}/deletion-preview`);
}

/**
 * PERMANENTLY ERASE A BOOKING. Owner only, any status, no undo — see the
 * doc comment on `DeleteBookingDto`. POST, not DELETE: a named business
 * action with a mandatory reason, same convention as `cancel`/`revise-value`.
 */
export function deleteBooking(
  id: string,
  payload: DeleteBookingDto,
): Promise<DeleteBookingResultDto> {
  return apiFetch<DeleteBookingResultDto>(`/bookings/${encodeURIComponent(id)}/delete`, {
    method: 'POST',
    body: payload,
  });
}

/** The Owner's read-only "Deleted bookings" list — Settings/Audit log. */
export function listDeletedBookings(): Promise<DeletedBookingArchiveDto[]> {
  return apiFetch<DeletedBookingArchiveDto[]>('/deleted-bookings');
}

/**
 * Record a payment.
 *
 * `idempotencyKey` is optional and travels as a header. Without one the server
 * fingerprints the request itself, which makes a plain double-click safe — but
 * that fingerprint is (booking, amount, mode, reference, date, status), so two
 * GENUINELY separate cash payments of the same amount on the same day with no
 * reference would be collapsed into one. Sending a key minted per dialog
 * session distinguishes "the user clicked twice" from "the user is recording a
 * second payment", which the fingerprint alone cannot do.
 */
export function recordPayment(
  payload: RecordPaymentDto,
  idempotencyKey?: string,
): Promise<PaymentDto> {
  return apiFetch<PaymentDto>('/payments', {
    method: 'POST',
    body: payload,
    ...(idempotencyKey !== undefined ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
  });
}

/**
 * Move an IN_CLEARING cheque off the waiting rung.
 *
 * POST and not PATCH, matching `revise-value` and `cancel`: these are named
 * business actions with a mandatory reason written to the audit trail, not
 * partial edits of a payment row. A payment is otherwise immutable and there is
 * still no PUT/PATCH/DELETE against one anywhere in this API.
 *
 * BOTH RESOLVE TO `unknown` ON PURPOSE. The useful figures after the
 * transition — `paidToDate`, `remaining`, the booking's payment state — are
 * re-read from the ledger endpoint, which owns them. The only things the caller
 * takes from this reply are the resulting status and an overpayment flag, both
 * read defensively by `readChequeActionOutcome`, so a field the server names
 * differently degrades to "not quoted" rather than to a type lie.
 *
 * Guarded by PAYMENTS_RECEIPTS:FULL on the server (Owner, Admin, Accounts) —
 * the class-level guard on the payments controller.
 */
export function clearChequePayment(
  paymentId: string,
  payload: ChequeActionRequestDto,
): Promise<unknown> {
  return apiFetch<unknown>(`/payments/${encodeURIComponent(paymentId)}/clear`, {
    method: 'POST',
    body: payload,
  });
}

export function bounceChequePayment(
  paymentId: string,
  payload: ChequeActionRequestDto,
): Promise<unknown> {
  return apiFetch<unknown>(`/payments/${encodeURIComponent(paymentId)}/bounce`, {
    method: 'POST',
    body: payload,
  });
}

/* ── payment ledger & receipts (read-only) ────────────────────────────────── */

/**
 * The whole ledger for one booking, oldest first, WITH running balances.
 *
 * The balances and `remaining` are computed server-side from the payment rows.
 * They are never recomputed here: the booking's cached `paid` and its own ledger
 * disagreeing is the precise defect this flow exists to remove, and a third
 * in-browser copy of the arithmetic would reintroduce it.
 */
export function fetchPaymentLedger(
  bookingId: string,
  signal?: AbortSignal,
): Promise<PaymentLedgerDto> {
  return apiFetch<PaymentLedgerDto>(
    `/payments/ledger/${encodeURIComponent(bookingId)}`,
    signal ? { signal } : {},
  );
}

/** One receipt, by payment id or by receipt number. There is no write counterpart. */
export function fetchReceipt(idOrRef: string, signal?: AbortSignal): Promise<ReceiptDto> {
  return apiFetch<ReceiptDto>(
    `/payments/receipts/${encodeURIComponent(idOrRef)}`,
    signal ? { signal } : {},
  );
}

/* ── documents: edited in place, never versioned ──────────────────────────── */

/**
 * The voucher for a booking, if the token payment has raised one.
 *
 * There is no `createVoucher` here and there must not be: the server has no
 * POST /vouchers, because a voucher is raised inside the transaction of the
 * payment that reaches TOKEN_PAID. A "Generate voucher" button would be a
 * button for a route that does not exist.
 */
export function fetchVoucherForBooking(
  bookingId: string,
  signal?: AbortSignal,
): Promise<VoucherWithInvoiceDto> {
  return apiFetch<VoucherWithInvoiceDto>(
    `/vouchers/for-booking/${encodeURIComponent(bookingId)}`,
    signal ? { signal } : {},
  );
}

/** Patch the ONE voucher in place. 409 DOCUMENT_LOCKED once the booking completes. */
export function updateVoucher(id: string, payload: UpdateVoucherDto): Promise<VoucherDto> {
  return apiFetch<VoucherDto>(`/vouchers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
}

/** Audit trail. Field NAMES and who/when — NOT a set of downloadable versions. */
export function fetchVoucherEdits(
  id: string,
  signal?: AbortSignal,
): Promise<DocumentEditLogEntryDto[]> {
  return apiFetch<DocumentEditLogEntryDto[]>(
    `/vouchers/${encodeURIComponent(id)}/edits`,
    signal ? { signal } : {},
  );
}

export function fetchInvoiceForBooking(
  bookingId: string,
  signal?: AbortSignal,
): Promise<InvoiceDto> {
  return apiFetch<InvoiceDto>(
    `/invoices/for-booking/${encodeURIComponent(bookingId)}`,
    signal ? { signal } : {},
  );
}

/**
 * Patch the ONE invoice in place, INCLUDING after it has been downloaded.
 *
 * `UpdateInvoiceDto` carries no tax fields on purpose — the server REJECTS a
 * client-supplied `gstAmount` outright ("property gstAmount should not exist").
 * `total` is likewise recomputed server-side from cost/extras/discount.
 */
export function updateInvoice(id: string, payload: UpdateInvoiceDto): Promise<InvoiceDto> {
  return apiFetch<InvoiceDto>(`/invoices/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function fetchInvoiceEdits(
  id: string,
  signal?: AbortSignal,
): Promise<DocumentEditLogEntryDto[]> {
  return apiFetch<DocumentEditLogEntryDto[]>(
    `/invoices/${encodeURIComponent(id)}/edits`,
    signal ? { signal } : {},
  );
}

/* ── share links: "Send via WhatsApp" ─────────────────────────────────────── */

/**
 * `/<kind>/<id>/share` — the base path every share route hangs off.
 *
 * A `byId`-style helper for the same reason `byId` exists: `encodeURIComponent`
 * is not optional here. These routes accept the human ref as well as the uuid
 * ("INV-2607-0001", "RCP-2607-0004"), and a ref containing a slash would
 * otherwise silently address a different route.
 *
 * NOT interpolating `kind` through `encodeURIComponent`: it is a closed union of
 * literal path segments, one of which — `payments/receipts` — legitimately
 * CONTAINS the slash that makes it two segments. Encoding it would turn
 * `/payments/receipts/:id/share` into `/payments%2Freceipts/...`, which is a 404.
 *
 * `tours` is the one kind whose route does not fit the template at all: the
 * thing being shared is a tour's BROCHURE — a field on the tour, not a document
 * family of its own — so the server exposes it at
 * `/tours/:id/brochure/share`, with a segment between the id and `/share` that
 * no other kind has. Written as an explicit early return in the same style as
 * the note above, rather than as a per-kind suffix table that four kinds would
 * pass through empty: an irregular route should be readable HERE, at the one
 * place the path is assembled.
 */
function sharePath(kind: ShareDocumentKind, id: string): string {
  if (kind === 'tours') return `/tours/${encodeURIComponent(id)}/brochure/share`;
  return `/${kind}/${encodeURIComponent(id)}/share`;
}

/**
 * Mint a shareable link for one document.
 *
 * The server renders and FREEZES the PDF at this moment, so the customer opening
 * the link days later sees what was shared, not what the document has since been
 * edited into. Re-sharing after an edit therefore needs a fresh call — which is
 * what happens naturally, because staff press "Send via WhatsApp" again.
 *
 * The reply is the ONLY place the token appears; the frontend turns it into the
 * customer-facing URL itself via `buildShareUrl`. Guarded on the server by the
 * FULL level of whichever module owns that document type — minting a bearer URL
 * that needs no login is a new capability, not merely "share what I can see".
 */
export function createShareLink(kind: ShareDocumentKind, id: string): Promise<ShareLinkDto> {
  return apiFetch<ShareLinkDto>(sharePath(kind, id), { method: 'POST' });
}

/**
 * The links already minted for one document, for the revoke affordance.
 *
 * `token` is deliberately absent from every row — see `ShareLinkSummaryDto`.
 * This answers "what is still live against this document", not "what URL did I
 * send".
 */
export function fetchShareLinks(
  kind: ShareDocumentKind,
  id: string,
  signal?: AbortSignal,
): Promise<ShareLinkSummaryDto[]> {
  return apiFetch<ShareLinkSummaryDto[]>(sharePath(kind, id), signal ? { signal } : {});
}

/**
 * Revoke one link. Immediate — the row is looked up on every open, so a revoked
 * link stops working for a customer who is holding it already.
 *
 * Takes the document id AS WELL AS the link id: the server verifies the link
 * actually belongs to that document before touching it, so a caller cannot
 * revoke another document's link by guessing an id.
 */
export function revokeShareLink(
  kind: ShareDocumentKind,
  id: string,
  shareLinkId: string,
): Promise<void> {
  return apiFetch<void>(`${sharePath(kind, id)}/${encodeURIComponent(shareLinkId)}`, {
    method: 'DELETE',
  });
}

/* ── settings & roles (Owner-only) ────────────────────────────────────────── */

export function fetchPermissionMatrix(signal?: AbortSignal): Promise<PermissionMatrixDto> {
  return apiFetch<PermissionMatrixDto>('/permissions/matrix', signal ? { signal } : {});
}

/**
 * Save matrix edits. Responds with the committed matrix, so the caller can
 * render server truth rather than its own optimistic guess.
 */
export function updatePermissionMatrix(
  payload: PermissionMatrixUpdateDto,
): Promise<PermissionMatrixDto> {
  return apiFetch<PermissionMatrixDto>('/permissions/matrix', { method: 'PATCH', body: payload });
}

export function fetchTeam(signal?: AbortSignal): Promise<TeamMemberDto[]> {
  return apiFetch<TeamMemberDto[]>('/users', signal ? { signal } : {});
}

export function createUser(payload: CreateUserDto): Promise<TeamMemberDto> {
  return apiFetch<TeamMemberDto>('/users', { method: 'POST', body: payload });
}

export function updateUser(id: string, payload: UpdateUserDto): Promise<TeamMemberDto> {
  return apiFetch<TeamMemberDto>(`/users/${id}`, { method: 'PATCH', body: payload });
}

/**
 * Bank Details / Terms & Conditions / Booking Branch printed live on every GST
 * tax invoice — Owner-only (`SETTINGS_ROLES`).
 *
 * `upiQrDataUrl` comes back on the READ side only. It is a data URL the server
 * computes from the QR image bytes an Owner uploaded; it is never sent on the
 * write side, because accepting a client-supplied data URL would let one
 * Owner-session paste arbitrary markup into every future invoice's HTML. The
 * image is replaced only through `uploadInvoiceQrImage` below.
 */
export function fetchInvoiceSettings(signal?: AbortSignal): Promise<InvoiceSettingsDto> {
  return apiFetch<InvoiceSettingsDto>('/settings/invoice-config', signal ? { signal } : {});
}

export function updateInvoiceSettings(
  payload: UpdateInvoiceSettingsDto,
): Promise<InvoiceSettingsDto> {
  return apiFetch<InvoiceSettingsDto>('/settings/invoice-config', { method: 'PUT', body: payload });
}

/**
 * The multipart part name the QR upload route reads the file from.
 *
 * Named here rather than inlined because it is one half of a contract whose
 * other half lives in the server's `FileInterceptor(...)` — a mismatch fails as
 * "no file was uploaded", not as a type error, so it is worth being able to
 * find every reference to it at once.
 */
export const QR_IMAGE_FORM_FIELD = 'file';

/**
 * Replace the payment QR printed on future invoices with a real uploaded image.
 *
 * `multipart/form-data`, not base64-in-JSON: the API's JSON body limit is far
 * below the 512 KB an image is allowed to be, so a base64 upload would be
 * rejected by the body parser before any of our own validation ran.
 *
 * Responds with the committed settings row, so the caller renders the QR the
 * server actually stored rather than a local preview of the file it sent —
 * which is the only way the Owner finds out that, say, the server re-encoded or
 * rejected part of what they picked.
 */
export function uploadInvoiceQrImage(file: File): Promise<InvoiceSettingsDto> {
  const form = new FormData();
  form.append(QR_IMAGE_FORM_FIELD, file, file.name);
  return apiFetch<InvoiceSettingsDto>('/settings/invoice-config/qr-image', {
    method: 'POST',
    body: form,
  });
}

/* ── website media (Cloudflare R2) ───────────────────────────────────────── */

/**
 * Mints a presigned PUT for a NEW object. This is the only call that goes
 * through the API — the actual bytes go straight from the browser to R2
 * (`MediaUploader` PUTs to `uploadUrl` itself), never through this backend.
 */
export function createMediaUploadUrl(input: CreateUploadUrlDto): Promise<UploadUrlDto> {
  return apiFetch<UploadUrlDto>('/media/upload-url', { method: 'POST', body: input });
}
